import { useCallback, useEffect, useMemo, useState } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useTemplateCatalog } from "@/hooks/useTemplateCatalog";
import { USER_ROUTES } from "@/routes";
import { OnboardingSessionChanged, OnboardingUnsavedChanges, useOnboardingStore } from "@/stores/onboardingStore";
import { usePlanSelectionStore } from "@/stores/planSelectionStore";
import { getEventPreviewToken } from "@/services/api/eventSettings";
import { onboardingErrorOf } from "@/services/api/onboarding";
import { WizardShell } from "@/components/customer/onboarding/WizardShell";
import { LivePreview } from "@/components/customer/onboarding/LivePreview";
import { TypeChangeConfirmDialog } from "@/components/customer/onboarding/TypeChangeConfirmDialog";
import { DateStep, EntryStep, PeopleStep, TypeStep } from "@/components/customer/onboarding/steps/AboutSteps";
import {
  AddedChip,
  CombinedStep,
  MomentLocationStep,
  MomentTimeStep,
  RecapStep,
  StructureStep,
} from "@/components/customer/onboarding/steps/ProgramSteps";
import {
  GeneratingStep,
  PreviewStep,
  RsvpDeadlineStep,
  RsvpQuestionsStep,
} from "@/components/customer/onboarding/steps/FinishSteps";
import {
  apiStepFor,
  groupOf,
  isSkippable,
  momentOf,
  nextStep,
  prevStep,
  progressOf,
  resolveStep,
  type StepId,
} from "@/lib/onboarding/stepMachine";
import { hasLetter, isValidTime, parseDay, rsvpDeadlineError, rsvpDefault, structureOptions } from "@/lib/onboarding/defaults";
import type { PreviewSource } from "@/lib/onboarding/buildPreviewEvent";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import {
  MOMENT_SLOTS,
  type CombinedAnswer,
  type CompleteVia,
  type MomentSlot,
  type OnboardingAnswers,
  type OnboardingPatch,
  type OnboardingType,
  type PlanChoice,
} from "@/lib/onboarding/types";

type FieldErrors = Record<string, boolean>;

const PRIMARY = "h-12 w-full bg-gradient-to-r from-pink-500 to-rose-500 text-base text-white hover:from-pink-600 hover:to-rose-600";

/**
 * JUB-154: event creation wizard (V1). One question per screen; every answer
 * is saved as it is given; the step lives in the URL; the draft event exists
 * from the first screen and the wizard ends only on an explicit exit
 * (continue to the editor, view as a guest, checkout from the preview).
 */
export default function OnboardingWizardPage() {
  const { uuid, step: stepParam } = useParams<{ uuid?: string; step?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const locale = useRouteLocale();

  usePageMeta({
    title: t("onboarding.metaTitle"),
    description: t("onboarding.metaTitle"),
    locale: locale,
    path: location.pathname,
    noindex: true,
  });
  const flags = useFeatureFlags();
  // JUB-213: the designs the reward screen offers and the preview resolves.
  const templateCatalog = useTemplateCatalog();

  const snapshot = useOnboardingStore((s) => s.snapshot);
  const answers = useOnboardingStore((s) => s.answers);
  const saveStatus = useOnboardingStore((s) => s.saveStatus);
  const store = useOnboardingStore.getState;

  const buyPlan = searchParams.get("buyPlan");
  const storedPlan = usePlanSelectionStore((s) => s.selectedPlan);
  const pendingPlan: PlanChoice | null =
    buyPlan === "standard" || buyPlan === "premium" ? buyPlan : storedPlan && storedPlan !== "free" ? storedPlan : null;

  const [entryPhase, setEntryPhase] = useState<"entry" | "type">("entry");
  const [pendingType, setPendingType] = useState<OnboardingType | null>(null);
  const [confirmType, setConfirmType] = useState<OnboardingType | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [rsvpError, setRsvpError] = useState<string | null>(null);
  const [rsvpSuggested, setRsvpSuggested] = useState(false);
  const [sameAs, setSameAs] = useState<MomentSlot | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<"notFound" | "trashed" | "generic" | null>(null);

  const editorUrl = useCallback(
    (eventUuid: string, tab?: string) => `${USER_ROUTES.event(locale, eventUuid)}${tab ? `?tab=${tab}` : ""}`,
    [locale],
  );
  const stepUrl = useCallback(
    (eventUuid: string, step: StepId) => `${USER_ROUTES.onboarding(locale, eventUuid, step)}${location.search}`,
    [locale, location.search],
  );

  /* ------------------------------------------------------------ loading */

  useEffect(() => {
    if (uuid) return;
    // A fresh visit to the entry screen is a new attempt (new idempotency key).
    store().reset();
  }, [uuid, store]);

  useEffect(() => {
    // Starting is behind the flag; resuming an already started draft is not.
    if (!uuid && flags.loaded && !flags.isEnabled("onboarding_wizard")) {
      navigate(USER_ROUTES.events(locale), { replace: true });
    }
  }, [uuid, flags, navigate, locale]);

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    setLoadError(null);
    store()
      .load(uuid)
      .then((loaded) => {
        if (cancelled) return;
        if (loaded.status === "completed") {
          // Back from checkout or an old link: the wizard is over, the editor takes it from here.
          toast.info(t("onboarding.errors.completed"));
          navigate(editorUrl(loaded.uuid), { replace: true });
        }
      })
      .catch((error: unknown) => {
        if (cancelled || error instanceof OnboardingSessionChanged) return;
        const status = (error as { response?: { status?: number } })?.response?.status;
        setLoadError(status === 404 ? "notFound" : "generic");
      });
    return () => {
      cancelled = true;
    };
  }, [uuid, store, navigate, editorUrl, t]);

  const step: StepId | null = uuid && answers ? resolveStep(stepParam ?? snapshot?.step, answers) : null;

  useEffect(() => {
    if (uuid && step && step !== stepParam) navigate(stepUrl(uuid, step), { replace: true });
  }, [uuid, step, stepParam, navigate, stepUrl]);

  // Per-screen setup when the screen changes.
  useEffect(() => {
    setErrors({});
    setRsvpError(null);
    setSameAs(null);
    if (step === "rsvp-deadline" && answers?.date && !answers.rsvpDeadline) {
      const suggestion = rsvpDefault(answers.date);
      if (suggestion) {
        store().setAnswers({ rsvpDeadline: suggestion });
        setRsvpSuggested(true);
      }
    }
    // Only on screen change: answers are read as they are at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  /* ------------------------------------------------------------ helpers */

  const retrySaves = () => {
    store()
      .retry()
      .then(() => toast.success(t("onboarding.nav.saved")))
      .catch(() => toast.error(t("onboarding.nav.unsaved")));
  };

  const goTo = useCallback(
    (next: StepId, options: { replace?: boolean; added?: MomentSlot } = {}) => {
      if (!uuid) return;
      navigate(stepUrl(uuid, next), { replace: options.replace, state: options.added ? { added: options.added } : null });
    },
    [uuid, navigate, stepUrl],
  );

  const handleSaveError = (error: unknown) => {
    // Work of a previous event/session: nothing to show here.
    if (error instanceof OnboardingSessionChanged) return;
    if (error instanceof OnboardingUnsavedChanges) {
      toast.error(t("onboarding.nav.unsaved"));
      return;
    }
    const { code, errors: fieldErrors } = onboardingErrorOf(error);
    if (code === "onboarding_completed" && uuid) {
      toast.info(t("onboarding.errors.completed"));
      navigate(editorUrl(uuid), { replace: true });
      return;
    }
    if (code === "onboarding_event_trashed") {
      toast.error(t("onboarding.errors.trashed"));
      return;
    }
    const flagged = Object.keys(fieldErrors);
    if (flagged.length > 0) {
      setErrors(Object.fromEntries(flagged.map((f) => [f, true])));
      const reason = fieldErrors.date?.[0];
      if (step === "rsvp-deadline" && reason) setRsvpError(reason);
      return;
    }
    toast.error(t("onboarding.errors.generic"));
  };

  /** Save the current screen and move on. */
  const saveAndGo = async (
    body: Omit<OnboardingPatch, "position" | "progress">,
    predicted: Pick<OnboardingAnswers, "type" | "moments">,
    options: { added?: MomentSlot } = {},
  ) => {
    if (!step) return;
    const next = nextStep(step, predicted);
    setBusy(true);
    try {
      await store().save({ ...body, position: next ?? undefined, progress: next ? progressOf(next, predicted) : undefined });
      if (next) goTo(next, { added: options.added });
    } catch (error) {
      handleSaveError(error);
    } finally {
      setBusy(false);
    }
  };

  const skip = () => {
    if (!step || !answers) return;
    if (step === "date") store().setAnswers({ date: null, dateTbd: true });
    void saveAndGo({ ...apiStepFor(step), skip: true }, answers);
  };

  const back = () => {
    if (!uuid) {
      setEntryPhase("entry");
      return;
    }
    if (!step || !answers) return;
    if (step === "type") {
      navigate(USER_ROUTES.events(locale));
      return;
    }
    const prev = prevStep(step, answers);
    // Never step back into the auto-advancing Generating screen.
    if (prev) goTo(prev === "generating" ? "rsvp-questions" : prev);
  };

  /* ------------------------------------------------------------ type */

  const startWizard = async () => {
    if (!pendingType) return;
    setBusy(true);
    try {
      const created = await store().start(pendingType, locale);
      navigate(stepUrl(created.uuid, created.status === "completed" ? "preview" : "people"));
    } catch (error) {
      handleSaveError(error);
    } finally {
      setBusy(false);
    }
  };

  const applyType = async (type: OnboardingType, confirm: boolean) => {
    if (!uuid) return;
    setBusy(true);
    try {
      await store().save({ step: "type", data: { type, confirm }, done: true, position: "people" });
      goTo("people");
    } catch (error) {
      handleSaveError(error);
    } finally {
      setBusy(false);
    }
  };

  const continueType = () => {
    if (!answers || !snapshot) return;
    const chosen = pendingType ?? answers.type;
    if (chosen === answers.type) {
      goTo("people");
      return;
    }
    if (snapshot.completedSteps.includes("structure")) {
      setConfirmType(chosen);
      return;
    }
    void applyType(chosen, false);
  };

  /* ------------------------------------------------------------ continue */

  const continueStep = () => {
    if (!step || !answers) return;
    const moment = momentOf(step);

    switch (step) {
      case "type":
        continueType();
        return;
      case "people": {
        const missing: FieldErrors = {};
        if (answers.type !== "baptism") {
          if (!hasLetter(answers.bride)) missing.bride = true;
          if (!hasLetter(answers.groom)) missing.groom = true;
        }
        if (answers.type !== "wedding" && !hasLetter(answers.children[0])) missing.children = true;
        if (Object.keys(missing).length > 0) {
          setErrors(missing);
          return;
        }
        const data: Record<string, unknown> = { bride: answers.bride, groom: answers.groom, children: answers.children.filter((c) => c.trim()) };
        if (answers.parents) data.parents = answers.parents;
        void saveAndGo({ step: "people", data, done: true }, answers);
        return;
      }
      case "date":
        if (!answers.date) {
          if (answers.dateTbd) {
            void saveAndGo({ step: "date", data: { tbd: true }, done: true }, answers);
          } else {
            setErrors({ date: true });
          }
          return;
        }
        if (!parseDay(answers.date)) {
          setErrors({ date: true });
          return;
        }
        void saveAndGo({ step: "date", data: { date: answers.date }, done: true }, answers);
        return;
      case "combined":
        if (!answers.combined) {
          setErrors({ combined: true });
          return;
        }
        void saveAndGo(
          {
            step: "structure",
            data: { combined: answers.combined, moments: structureOptions(answers.type).filter((s) => answers.moments[s].enabled) },
            done: true,
          },
          answers,
        );
        return;
      case "structure": {
        if (!MOMENT_SLOTS.some((s) => answers.moments[s].enabled)) {
          setErrors({ moments: true });
          return;
        }
        const data: Record<string, unknown> = { moments: MOMENT_SLOTS.filter((s) => answers.moments[s].enabled) };
        if (answers.type === "wedding_baptism") data.combined = answers.combined;
        void saveAndGo({ step: "structure", data, done: true }, answers);
        return;
      }
      case "recap":
      case "rsvp-questions":
        void saveAndGo({ ...apiStepFor(step), done: true }, answers);
        return;
      case "rsvp-deadline": {
        if (!answers.date || !answers.rsvpDeadline) {
          skip();
          return;
        }
        const problem = rsvpDeadlineError(answers.rsvpDeadline, answers.date);
        if (problem) {
          setRsvpError(problem);
          return;
        }
        void saveAndGo({ step: "rsvp_deadline", data: { date: answers.rsvpDeadline }, done: true }, answers);
        return;
      }
      default:
        break;
    }

    if (moment?.part === "time") {
      const time = answers.moments[moment.slot].time;
      if (!time) {
        skip();
        return;
      }
      if (!isValidTime(time)) {
        setErrors({ time: true });
        return;
      }
      void saveAndGo({ step: "moment", data: { slot: moment.slot, part: "time", time }, done: true }, answers);
    } else if (moment?.part === "location") {
      const m = answers.moments[moment.slot];
      if (sameAs) {
        void saveAndGo({ step: "moment", data: { slot: moment.slot, part: "location", sameAs }, done: true }, answers, { added: moment.slot });
      } else if (!m.venue?.trim() && !m.address?.trim() && !m.city?.trim()) {
        skip();
      } else {
        void saveAndGo(
          { step: "moment", data: { slot: moment.slot, part: "location", venue: m.venue, address: m.address, city: m.city }, done: true },
          answers,
          { added: moment.slot },
        );
      }
    }
  };

  /* ------------------------------------------------------------ preview exits */

  const exit = async (via: CompleteVia, then: (eventUuid: string) => void) => {
    if (!uuid) return;
    setBusy(true);
    try {
      await store().complete(via);
      then(uuid);
    } catch (error) {
      handleSaveError(error);
    } finally {
      setBusy(false);
    }
  };

  const onGuestView = () =>
    exit("guest_view", async (eventUuid) => {
      const slug = useOnboardingStore.getState().snapshot?.url ?? "";
      try {
        const { token } = await getEventPreviewToken(eventUuid);
        navigate(`/@${slug}?preview=${encodeURIComponent(token)}`);
      } catch {
        navigate(editorUrl(eventUuid, "landing"));
      }
    });

  const onCheckout = (plan: PlanChoice) =>
    void (async () => {
      if (!uuid) return;
      try {
        await store().save({ step: "package", data: { ...(answers?.packageAnswers ?? {}), choice: plan }, done: true });
      } catch (error) {
        handleSaveError(error);
        return;
      }
      // JUB-157: a paid plan is always bought for one event — this one.
      await exit("checkout", (eventUuid) => {
        const params = new URLSearchParams({ plan, event: eventUuid });
        navigate(`/${locale}/payment?${params.toString()}`);
      });
    })();

  const onSelectDesign = (theme: string) => {
    store()
      .setTheme(theme)
      .catch((error: unknown) => {
        if (error instanceof OnboardingSessionChanged) return;
        toast.error(t("onboarding.preview.designError"));
      });
  };

  const onPackageAnswers = (packageAnswers: NonNullable<OnboardingAnswers["packageAnswers"]>) => {
    store().setAnswers({ packageAnswers });
    store().save({ step: "package", data: packageAnswers, done: true }).catch(handleSaveError);
  };

  /* ------------------------------------------------------------ render */

  const previewSource: PreviewSource | null = useMemo(
    () =>
      snapshot && answers
        ? {
            uuid: snapshot.uuid,
            url: snapshot.url,
            theme: snapshot.theme,
            language: snapshot.language,
            answers,
            catalog: templateCatalog,
          }
        : null,
    [snapshot, answers, templateCatalog],
  );

  if (loadError) {
    return (
      <WizardShell stepId="error" group={null} saveStatus="idle" onBack={() => navigate(USER_ROUTES.events(locale))}>
        <p className="text-lg" role="alert">
          {t(`onboarding.errors.${loadError}`)}
        </p>
      </WizardShell>
    );
  }

  // ---- before the draft exists: W00 + W01
  if (!uuid) {
    if (entryPhase === "entry") {
      return (
        <WizardShell
          stepId="entry"
          group={null}
          saveStatus="idle"
          onSubmit={() => setEntryPhase("type")}
          footer={
            <div className="flex flex-col gap-2">
              <Button type="submit" className={PRIMARY} data-testid="onboarding-entry-start">
                {t("onboarding.entry.start")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate(USER_ROUTES.events(locale))} data-testid="onboarding-entry-cancel">
                {t("onboarding.entry.cancel")}
              </Button>
            </div>
          }
        >
          <EntryStep />
        </WizardShell>
      );
    }
    return (
      <WizardShell
        stepId="type"
        group="about"
        saveStatus={saveStatus}
      onRetry={retrySaves}
        onBack={back}
        onSubmit={() => void startWizard()}
        footer={
          <Button type="submit" className={PRIMARY} disabled={!pendingType || busy} data-testid="onboarding-continue">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {t("onboarding.nav.continue")}
          </Button>
        }
      >
        <TypeStep value={pendingType} onSelect={setPendingType} />
      </WizardShell>
    );
  }

  if (!step || !answers || !snapshot || !previewSource) {
    return (
      <div className="flex min-h-screen items-center justify-center" role="status">
        <Loader2 className="h-6 w-6 animate-spin text-pink-500" aria-label={t("common.loading")} />
      </div>
    );
  }

  const moment = momentOf(step);
  const added = (location.state as { added?: MomentSlot } | null)?.added;
  const sameCandidate =
    moment?.part === "location"
      ? [...MOMENT_SLOTS.slice(0, MOMENT_SLOTS.indexOf(moment.slot))]
          .reverse()
          .find((s) => answers.moments[s].enabled && answers.moments[s].venue) ?? null
      : null;

  if (step === "generating") {
    return (
      <WizardShell stepId={step} group="done" saveStatus={saveStatus}
      onRetry={retrySaves}>
        <GeneratingStep
          onDone={() => {
            store()
              .save({ step: "generating", done: true, position: "preview", progress: progressOf("preview", answers) })
              .catch(() => undefined);
            goTo("preview", { replace: true });
          }}
        />
      </WizardShell>
    );
  }

  if (step === "preview") {
    const name = answers.children[0] ?? "";
    return (
      <WizardShell stepId={step} group="done" saveStatus={saveStatus}
      onRetry={retrySaves} onBack={back}>
        <PreviewStep
          source={previewSource}
          title={t(`onboarding.preview.title.${answers.type}`, { name })}
          busy={busy}
          pendingPlan={pendingPlan}
          onContinue={() => void exit("editor", (id) => navigate(editorUrl(id), { replace: true }))}
          onGuestView={() => void onGuestView()}
          onDesign={() => void exit("editor", (id) => navigate(editorUrl(id, "landing"), { replace: true }))}
          onSelectDesign={onSelectDesign}
          onLater={() => navigate(USER_ROUTES.events(locale))}
          onEditAnswers={() => goTo("recap")}
          onCheckout={onCheckout}
          onPackageAnswers={onPackageAnswers}
        />
      </WizardShell>
    );
  }

  const primaryLabel = step === "recap" ? t("onboarding.recap.looksGood") : t("onboarding.nav.continue");
  const skipLabel = step === "rsvp-deadline" ? t("onboarding.rsvpDeadline.later") : t("onboarding.nav.later");
  const showSkip = isSkippable(step) && step !== "date" && !(step === "rsvp-deadline" && !answers.date);

  const footer = (
    <div className="flex flex-col gap-2">
      <Button type="submit" className={PRIMARY} disabled={busy} data-testid="onboarding-continue">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {primaryLabel}
      </Button>
      {showSkip && (
        <Button type="button" variant="ghost" onClick={skip} disabled={busy} data-testid="onboarding-skip">
          {skipLabel}
        </Button>
      )}
    </div>
  );

  const updatePeople = (changes: Partial<Pick<OnboardingAnswers, "bride" | "groom" | "children" | "parents">>) => {
    store().setAnswers(changes);
    setErrors({});
    store().saveDebounced({ step: "people", data: changes });
  };

  const selectCombined = (value: CombinedAnswer) => {
    store().setAnswers({ combined: value });
    store().setMoment("religious", { enabled: value !== "none" });
    store().setMoment("baptism", { enabled: value === "separate" });
    setErrors({});
  };

  return (
    <WizardShell
      stepId={step}
      group={groupOf(step)}
      saveStatus={saveStatus}
      onRetry={retrySaves}
      onBack={back}
      onSubmit={continueStep}
      footer={footer}
      preview={
        step === "type" ? undefined : (
          <LivePreview
            source={previewSource}
            focus={groupOf(step) === "program" ? "details" : groupOf(step) === "rsvp" ? "rsvp" : "top"}
          />
        )
      }
    >
      {added && answers.moments[added] && step !== "recap" && <AddedChip slot={added} answers={answers} />}

      {step === "type" && <TypeStep value={pendingType ?? answers.type} onSelect={setPendingType} />}
      {step === "people" && <PeopleStep answers={answers} errors={errors} onChange={updatePeople} />}
      {step === "date" && (
        <DateStep
          answers={answers}
          locale={locale}
          invalid={Boolean(errors.date)}
          onChange={(date, tbd) => {
            setErrors({});
            store().setAnswers({ date, dateTbd: tbd });
          }}
        />
      )}
      {step === "combined" && <CombinedStep value={answers.combined} onSelect={selectCombined} />}
      {step === "structure" && (
        <StructureStep
          answers={answers}
          invalid={Boolean(errors.moments)}
          onToggle={(slot) => {
            setErrors({});
            store().setMoment(slot, { enabled: !answers.moments[slot].enabled });
          }}
        />
      )}
      {moment?.part === "time" && (
        <MomentTimeStep
          slot={moment.slot}
          answers={answers}
          invalid={Boolean(errors.time)}
          onChange={(time) => {
            setErrors({});
            store().setMoment(moment.slot, { time: time || null });
          }}
        />
      )}
      {moment?.part === "location" && (
        <MomentLocationStep
          slot={moment.slot}
          answers={answers}
          sameCandidate={sameCandidate}
          sameAs={sameAs}
          onSameAs={(source) => {
            setSameAs(source);
            if (source) {
              const from = answers.moments[source];
              store().setMoment(moment.slot, { venue: from.venue, address: from.address, city: from.city });
            }
          }}
          onChange={(changes) => {
            store().setMoment(moment.slot, changes);
            store().saveDebounced({ step: "moment", data: { slot: moment.slot, part: "location", ...changes } });
          }}
        />
      )}
      {step === "recap" && (
        <RecapStep answers={answers} onEdit={(slot) => goTo(slot ? (`moment-${slot}-time` as StepId) : answers.type === "wedding_baptism" ? "combined" : "structure")} />
      )}
      {step === "rsvp-deadline" && (
        <RsvpDeadlineStep
          answers={answers}
          suggested={rsvpSuggested}
          error={rsvpError}
          onChange={(date) => {
            setRsvpError(null);
            setRsvpSuggested(false);
            store().setAnswers({ rsvpDeadline: date });
          }}
        />
      )}
      {step === "rsvp-questions" && <RsvpQuestionsStep />}

      <TypeChangeConfirmDialog
        open={confirmType !== null}
        current={answers.type}
        onCancel={() => {
          setConfirmType(null);
          setPendingType(answers.type);
        }}
        onConfirm={() => {
          const chosen = confirmType;
          setConfirmType(null);
          if (chosen) void applyType(chosen, true);
        }}
      />
    </WizardShell>
  );
}
